/** @jsxImportSource @emotion/react */

import { FC, useMemo } from "react";
import { formatNumber, formatPrice } from "../../../../utils/formatNumber";
import { CoverageTags, Result } from "../../../../graphql/__GENERATED__/types";
import ProviderLogo from "../../../../components/ProviderLogos/ProviderLogo";
import { useUIStore } from "../../../../hooks/useUIStore";

import DetailsTable from "../../../../components/Details/components/Main/Table/DetailsTable";

interface SentQuotationsTableProps {
  results: Result[];
}

const CoverageTagsNames = {
  [CoverageTags.Additional]: "Adicional",
  [CoverageTags.Basic]: "Básica",
  [CoverageTags.PhytosanitaryTreatment]: "Tratamento Fitossanitário",
  [CoverageTags.PlantLife]: "Vida da Planta",
  [CoverageTags.ProductionLoss]: "Perda de Produção",
};

const firstCoverages = [CoverageTags.Basic, CoverageTags.PlantLife];

const converageIndex = (coverageTags: CoverageTags) =>
  firstCoverages.includes(coverageTags) ? 0 : 1;

const SentQuotationsTable: FC<SentQuotationsTableProps> = ({ results }) => {
  const { documentColor } = useUIStore();

  const coverageTags = useMemo(
    () =>
      results.flatMap((result) =>
        result?.coverages?.map((coverage) => coverage?.coverageTags)
      ),
    [results]
  );

  const shouldDisplayProductivity = useMemo(() => {
    return results.some((result) => result.guaranteedProductivity);
  }, [results]);

  const uniqueCoverageTags = useMemo(() => {
    return coverageTags
      .filter((tag, index) => coverageTags.indexOf(tag) === index)
      .sort((a, b) => converageIndex(a!) - converageIndex(b!));
  }, [coverageTags]);

  const tableColumns = useMemo(
    () =>
      results.map((result) => {
        const coverageTagColumns = uniqueCoverageTags.flatMap((tag) => {
          const coverage = result.coverages?.find(
            (c) => c?.coverageTags === tag
          );
          const lmiValue = coverage?.lmi;
          return [
            {
              label: `LMI [${
                CoverageTagsNames[tag as keyof typeof CoverageTagsNames]
              }]`,
              value: lmiValue ? formatPrice(lmiValue) : "-",
            },
          ];
        });

        const productivityData: { value: string; label: string }[] = [];
        if (shouldDisplayProductivity) {
          productivityData.push({
            label: "Produtividade Garantida",
            value: result.guaranteedProductivity
              ? `${formatNumber(result.guaranteedProductivity, 2, 2)} sacas/ha`
              : "-",
          });
        }
        if (!result.amounts) return [];
        return [
          {
            label: "Seguradora",
            value: (
              <ProviderLogo provider={result.provider!} color={documentColor} />
            ),
          },
          ...productivityData,
          ...coverageTagColumns,
          {
            label: "Prêmio",
            value: formatPrice(result.amounts!.totalPremium),
          },
          {
            label: "Subvenção Estadual*",
            value: result.amounts?.stateSubvention
              ? ` - ${formatPrice(result.amounts?.stateSubvention)}`
              : "-",
          },
          {
            label: "Subvenção Federal",
            value: result.amounts?.federalSubvention
              ? ` - ${formatPrice(result.amounts?.federalSubvention)}`
              : "-",
          },
          {
            label: "Custo Total",
            value: formatPrice(result.amounts!.finalPremium),
          },
          {
            label: "Forma de Pagamento",
            value: `${result.amounts!.maxPayments} X ${formatPrice(
              result.amounts!.finalPremium! / result.amounts!.maxPayments!
            )}`,
          },
        ];
      }),
    [uniqueCoverageTags, shouldDisplayProductivity]
  );

  if (!(tableColumns && tableColumns.length)) return null;
  return <DetailsTable data={tableColumns} />;
};

export default SentQuotationsTable;
